<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">Pasado y Futuro</h1>
          <p class="text-sm text-gray-400 mt-1">
            Gestioná los Jóvenes Bochófilos y los Jugadores Históricos
          </p>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex items-center justify-end gap-2 sm:flex sm:items-center sm:gap-4 mb-6">
        <button @click="activeTab = 'jóvenes'" :class="[
          'px-4 sm:px-6 py-2 rounded-full font-semibold text-sm sm:text-base transition-all',
          activeTab === 'jóvenes'
            ? 'bg-bochas-rojo text-white shadow-lg'
            : 'bg-white/10 text-gray-300 hover:bg-white/20',
        ]">
          🌟 Jóvenes Bochófilos
        </button>
        <button @click="activeTab = 'referentes'" :class="[
          'px-4 sm:px-6 py-2 rounded-full font-semibold text-sm sm:text-base transition-all',
          activeTab === 'referentes'
            ? 'bg-bochas-azul text-white shadow-lg'
            : 'bg-white/10 text-gray-300 hover:bg-white/20',
        ]">
          🏛️ Jugador Histórico
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <template v-else>
        <!-- JÓVENES -->
        <div v-if="activeTab === 'jóvenes'">
          <div class="flex mb-4 sm:justify-end">
            <router-link to="/admin/pasado-futuro/joven/nuevo" class="btn-bochas-rojo w-full text-center text-sm sm:w-auto">
              + Nuevo joven bochófilo
            </router-link>
          </div>
          <div v-if="jóvenes.length === 0" class="text-center py-8 text-gray-400">
            Aún no hay jóvenes bochófilos cargados.
          </div>
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="j in jóvenes" :key="j.id" class="card-fbochas p-4">
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden bg-bochas-bg">
                  <img v-if="j.photo_url" :src="j.photo_url" :alt="j.name" class="w-full h-full object-cover" />
                  <div v-else
                    class="w-full h-full bg-gradient-to-br from-bochas-azul to-bochas-red-deep flex items-center justify-center text-white font-bold">
                    {{ initials(j.name) }}
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-white font-semibold truncate">{{ j.name }}</h3>
                  <p class="text-xs text-gray-400 truncate">{{ j.association || "—" }}</p>
                  <p class="text-xs text-bochas-rojo mt-1">{{ careerPeriod(j, `${j.yearsPlaying} años de juego`) }}</p>
                </div>
              </div>
              <div class="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-white/5">
                <router-link
                  :to="`/admin/pasado-futuro/joven/editar/${j.id}`"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                 title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
                <button
                  @click="onDeleteJoven(j)"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                 title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
              </div>
            </div>
          </div>
        </div>

        <!-- REFERENTES -->
        <div v-if="activeTab === 'referentes'">
          <div class="flex mb-4 sm:justify-end">
            <router-link to="/admin/pasado-futuro/referente/nuevo" class="btn-bochas-azul w-full text-center text-sm sm:w-auto">
              + Nuevo jugador histórico
            </router-link>
          </div>
          <div v-if="referentes.length === 0" class="text-center py-8 text-gray-400">
            Aún no hay jugadores históricos cargados.
          </div>
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="r in referentes" :key="r.id" class="card-fbochas p-4">
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0 w-16 h-16 rounded-full overflow-hidden bg-bochas-bg">
                  <img v-if="r.photo_url" :src="r.photo_url" :alt="r.name" class="w-full h-full object-cover" />
                  <div v-else
                    class="w-full h-full bg-gradient-to-br from-bochas-azul to-bochas-rojo flex items-center justify-center text-white font-bold">
                    {{ initials(r.name) }}
                  </div>
                </div>
                <div class="flex-1 min-w-0">
                  <h3 class="text-white font-semibold truncate">{{ r.name }}</h3>
                  <p class="text-xs text-gray-400 truncate">{{ r.association || "—" }}</p>
                  <p class="text-[10px] text-gray-500 mt-1">
                    {{ careerPeriod(r, formatLife(r)) }}
                  </p>
                </div>
              </div>
              <div class="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-white/5">
                <router-link
                  :to="`/admin/pasado-futuro/referente/editar/${r.id}`"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                 title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
                <button
                  @click="onDeleteReferente(r)"
                  class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                 title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { listarJovenesAdmin, eliminarJoven } from "@/services/youngPlayers/youngPlayersService";
import { listarReferentesAdmin, eliminarReferente } from "@/services/referentes/referentesService";
import { formatDate } from "@/utils/date";
import { careerPeriod } from "@/utils/playerCareer";
import { useConfirm } from "@/composables/useConfirm";

const jóvenes = ref([]);
const referentes = ref([]);
const loading = ref(true);
const error = ref("");
const activeTab = ref("jóvenes");
const { confirm } = useConfirm();

function initials(name) {
  if (!name) return "";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function formatLife(player) {
  const birth = player.birthDate ? formatDate(player.birthDate) : "";
  const death = player.deathDate ? formatDate(player.deathDate) : "";
  return [birth, death].filter(Boolean).join(" — ");
}

async function load() {
  loading.value = true;
  try {
    const [jRes, rRes] = await Promise.all([
      listarJovenesAdmin(),
      listarReferentesAdmin(),
    ]);
    jóvenes.value = jRes?.items || [];
    referentes.value = rRes?.items || [];
  } catch (err) {
    console.error(err);
  } finally {
    loading.value = false;
  }
}

async function onDeleteJoven(j) {
  const confirmed = await confirm({
    title: "Eliminar",
    message: `¿Eliminar a ${j.name}?`,
  });
  if (!confirmed) return;
  try {
    await eliminarJoven(j.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo eliminar.";
  }
}

async function onDeleteReferente(r) {
  const confirmed = await confirm({
    title: "Eliminar",
    message: `¿Eliminar a ${r.name}?`,
  });
  if (!confirmed) return;
  try {
    await eliminarReferente(r.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || "No se pudo eliminar.";
  }
}

onMounted(load);
</script>
